import React, { useState, useEffect, useContext } from "react";

import {
    Factory,
    ArrowRight,
    Sparkles,
    ShieldCheck,
    BarChart3,
    XCircle,
    CheckCircle,
    Download,
    Database,
    Bot,
    Settings,
    LineChart,
    Camera,
    PackageSearch,
    Warehouse,
    Truck,
    ShoppingCart,
    BadgeCheck,
    Wrench,
    Activity,
    Gauge,
} from "lucide-react";

import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { isValidPhoneNumber } from "react-phone-number-input";

import Head from "../../Components/Layout/Head";
import FooterNew from "../../Components/Layout/FooterNew";
import { CountryContext } from "../../CountryContext";
import ApiPath from "../../Components/Common/Apiurl";

import AOS from "aos";
import "aos/dist/aos.css";

const countryOptions = [
    "Australia",
    "Germany",
    "India",
    "New Zealand",
    "United States",
    "Other",
];

const manufacturingFeatures = [
    {
        id: 1,
        icon: <LineChart size={20} />,
        name: "Production Demand Forecasting & Schedule Optimization",
        purpose: "Production planning / plant operations",
        currentChallenge:
            "Production planning is often handled through Excel sheets, past assumptions and manual coordination across demand, capacity and delivery commitments.",
        aiAction:
            "AI analyzes historical orders, seasonal demand, machine capacity and production constraints to recommend optimized production schedules.",
        benefit:
            "Improves throughput, capacity utilization, production discipline and delivery planning.",
        implementation:
            "Can connect with ERP, order history, Excel production sheets, machine capacity data and dispatch commitments.",
    },
    {
        id: 2,
        icon: <Camera size={20} />,
        name: "Computer Vision for Assembly Line Monitoring",
        purpose: "Assembly line monitoring / shop-floor visibility",
        currentChallenge:
            "Line issues, bottlenecks and abnormal activities are identified late because monitoring depends on manual supervision.",
        aiAction:
            "Computer vision monitors assembly lines for bottlenecks, defects, missing steps and process deviations.",
        benefit:
            "Enables faster issue detection, reduced rework and stronger shop-floor visibility.",
        implementation:
            "Can use existing cameras where feasible or dedicated cameras installed at key production checkpoints.",
    },
    {
        id: 3,
        icon: <Gauge size={20} />,
        name: "Process Optimization AI",
        purpose: "Production process improvement",
        currentChallenge:
            "Process analysis is periodic and manual, making it difficult to identify cycle-time losses and workflow bottlenecks in real time.",
        aiAction:
            "AI analyzes machine, operator, shift and workflow data to recommend better process flow and improvement areas.",
        benefit:
            "Helps reduce cycle time, improve production discipline and support data-backed operational decisions.",
        implementation:
            "Can use machine logs, production records, operator shift data, downtime logs and quality records.",
    },
    {
        id: 4,
        icon: <PackageSearch size={20} />,
        name: "Demand Forecasting AI for Inventory",
        purpose: "Inventory planning / raw material management",
        currentChallenge:
            "Raw material and finished goods planning is often based on manual review, leading to overstock, stockouts or urgent procurement.",
        aiAction:
            "AI predicts material requirements based on consumption trends, production plans, lead times and seasonal demand.",
        benefit:
            "Improves inventory planning, reduces stock pressure and supports better working capital control.",
        implementation:
            "Can connect with stock ledgers, ERP, Excel inventory sheets, purchase history and production planning data.",
    },
    {
        id: 5,
        icon: <Camera size={20} />,
        name: "Automated Stock Counting using Computer Vision + IoT",
        purpose: "Warehouse / store operations",
        currentChallenge:
            "Physical stock counting is manual, time-consuming and prone to mismatch between actual stock and system stock.",
        aiAction:
            "AI-assisted stock counting uses cameras, sensors, barcode, QR or RFID-linked movement data to support verification.",
        benefit:
            "Improves stock accuracy, speeds up audits and reduces dependency on manual counting.",
        implementation:
            "Can be implemented using barcode scanners, RFID, IoT sensors, cameras or hybrid verification workflows.",
    },
    {
        id: 6,
        icon: <Warehouse size={20} />,
        name: "Smart Warehouse Optimization",
        purpose: "Warehouse layout / picking / dispatch",
        currentChallenge:
            "Warehouse teams spend time searching stock, following inefficient picking paths and manually prioritizing dispatch.",
        aiAction:
            "AI suggests optimized storage locations, picking paths, movement patterns and dispatch priorities.",
        benefit:
            "Improves warehouse productivity, order fulfilment speed and dispatch discipline.",
        implementation:
            "Can work with bin locations, SKU movement data, dispatch priority, stock aging and warehouse layout information.",
    },
    {
        id: 7,
        icon: <Truck size={20} />,
        name: "Supplier Risk Analytics",
        purpose: "Procurement / vendor management",
        currentChallenge:
            "Supplier performance is reviewed manually, making it difficult to identify vendor risk early.",
        aiAction:
            "AI analyzes supplier delivery delays, rejection rates, pricing history, responsiveness and dependency levels.",
        benefit:
            "Improves vendor control, reduces supply disruption and supports alternative supplier planning.",
        implementation:
            "Can generate supplier scorecards using purchase orders, GRN records, rejection data and delivery history.",
    },
    {
        id: 8,
        icon: <LineChart size={20} />,
        name: "Raw Material Price Prediction Models",
        purpose: "Procurement planning / purchase timing",
        currentChallenge:
            "Raw material pricing is tracked manually, which makes purchase timing decisions dependent on limited visibility.",
        aiAction:
            "AI tracks price trends, purchase history and market indicators to suggest better buying windows.",
        benefit:
            "Supports smarter procurement decisions and reduces exposure to unfavorable price movement.",
        implementation:
            "Can use purchase history, supplier quotes, commodity price trends and market reference data.",
    },
    {
        id: 9,
        icon: <ShoppingCart size={20} />,
        name: "AI-Powered Purchase Automation",
        purpose: "Purchase request / PO workflow",
        currentChallenge:
            "Purchase requests and purchase orders are manually prepared, checked and followed up for approval.",
        aiAction:
            "AI auto-generates purchase request or PO drafts based on stock levels, production demand and approval rules.",
        benefit:
            "Improves procurement cycle speed, reduces manual errors and creates better purchase discipline.",
        implementation:
            "Can connect with stock thresholds, supplier master, approval hierarchy, ERP purchase module and inventory planning data.",
    },
    {
        id: 10,
        icon: <BadgeCheck size={20} />,
        name: "Computer Vision Quality Inspection",
        purpose: "Quality control / visual inspection",
        currentChallenge:
            "Manual inspection can miss visual defects, especially during high-volume production or repetitive inspection work.",
        aiAction:
            "Computer vision detects cracks, dents, missing components, shape issues, color mismatch or assembly defects.",
        benefit:
            "Improves quality consistency, reduces rework and identifies defects closer to the production stage.",
        implementation:
            "Can be deployed at inspection points using cameras, image datasets, defect labels and review workflows.",
    },
    {
        id: 11,
        icon: <Activity size={20} />,
        name: "Quality Anomaly Detection AI",
        purpose: "Quality analytics / defect prevention",
        currentChallenge:
            "Quality trends are often reviewed after defects increase, causing delayed corrective action.",
        aiAction:
            "AI monitors quality, machine and process parameters to detect abnormal patterns before defects rise.",
        benefit:
            "Supports early defect detection, reduced scrap risk and stronger process control.",
        implementation:
            "Can use inspection results, rejection logs, machine parameters, shift data and supplier lot information.",
    },
    {
        id: 12,
        icon: <Settings size={20} />,
        name: "Root Cause Analysis AI",
        purpose: "Defect analysis / corrective action",
        currentChallenge:
            "Root cause analysis takes time because teams manually correlate defects with machines, materials, operators and process changes.",
        aiAction:
            "AI correlates defect patterns with machine settings, supplier lots, shifts, operators and process events.",
        benefit:
            "Speeds up corrective action, supports evidence-based RCA and reduces repeat defects.",
        implementation:
            "Can analyze defect records, production batches, shift logs, maintenance history and quality inspection data.",
    },
    {
        id: 13,
        icon: <Wrench size={20} />,
        name: "Predictive Maintenance",
        purpose: "Maintenance planning / machine reliability",
        currentChallenge:
            "Maintenance teams often respond after machine breakdowns, causing unplanned downtime and production disruption.",
        aiAction:
            "AI predicts likely machine failure using sensor readings, maintenance history and usage patterns.",
        benefit:
            "Reduces unplanned downtime, improves machine availability and supports planned maintenance decisions.",
        implementation:
            "Can use vibration, temperature, energy, runtime, breakdown logs and maintenance records.",
    },
    {
        id: 14,
        icon: <Activity size={20} />,
        name: "Condition Monitoring AI",
        purpose: "Machine health monitoring",
        currentChallenge:
            "Machine health checks are performed through manual rounds or periodic inspections, leaving gaps between checks.",
        aiAction:
            "AI continuously monitors machine health signals and raises alerts when abnormal behavior is detected.",
        benefit:
            "Improves equipment lifespan, reduces emergency repairs and supports condition-based maintenance.",
        implementation:
            "Can connect with IoT sensors, PLC data, machine logs, temperature data, vibration data and energy usage.",
    },
    {
        id: 15,
        icon: <Wrench size={20} />,
        name: "AI-Driven Maintenance Scheduling",
        purpose: "Maintenance scheduling / service planning",
        currentChallenge:
            "Maintenance schedules are often fixed or manually planned without considering actual machine usage and production priority.",
        aiAction:
            "AI recommends maintenance schedules based on machine usage, risk level, breakdown probability and production plans.",
        benefit:
            "Reduces over-servicing, avoids under-servicing and improves maintenance planning efficiency.",
        implementation:
            "Can work with maintenance calendars, machine usage, spare availability, production schedules and downtime windows.",
    },
];

const valueAreas = [
    {
        icon: <Factory size={24} />,
        title: "Production Planning",
        text: "Forecast demand, optimize schedules, identify bottlenecks and improve capacity utilization.",
    },
    {
        icon: <PackageSearch size={24} />,
        title: "Inventory & Warehouse",
        text: "Improve stock visibility, reduce stock mismatches, optimize picking paths and support better dispatch planning.",
    },
    {
        icon: <BadgeCheck size={24} />,
        title: "Quality Intelligence",
        text: "Detect defects earlier, identify abnormal patterns and speed up root cause analysis.",
    },
    {
        icon: <Wrench size={24} />,
        title: "Maintenance Reliability",
        text: "Predict failures, monitor machine health and schedule maintenance based on real operating conditions.",
    },
];

const implementationAreas = [
    {
        icon: <Database size={22} />,
        title: "Connect Existing Systems",
        text: "AI can work with ERP, Excel sheets, SQL databases, machine logs, warehouse records, purchase data and quality records.",
    },
    {
        icon: <Camera size={22} />,
        title: "Use Cameras & IoT Where Needed",
        text: "Computer vision and sensor-based features can be added gradually for inspection, stock counting and machine monitoring.",
    },
    {
        icon: <Bot size={22} />,
        title: "Create Operations AI Assistants",
        text: "Role-based AI assistants can support plant managers, quality teams, procurement, warehouse and maintenance teams.",
    },
    {
        icon: <ShieldCheck size={22} />,
        title: "Secure Plant Data",
        text: "Private deployment, user roles, audit logs and controlled access can protect operational and production data.",
    },
];

const ManufacturingAI = () => {
    const [selectedCountry, setSelectedCountry] = useState("India");

    const country = useContext(CountryContext);
    const websiteURL = window.location.origin;

    const [formVisible, setFormVisible] = useState(true);
    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState("");
    const [phone, setPhone] = useState("");

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        country: "",
        message: "",
        firmType: "Manufacturing",
    });

    const [errors, setErrors] = useState({
        name: "",
        email: "",
        phone: "",
        country: "",
        firmType: "",
        message: "",
    });

    useEffect(() => {
        AOS.init({
            duration: 900,
            easing: "ease-out-cubic",
            once: true,
            offset: 80,
        });
    }, []);

    useEffect(() => {
        if (countryOptions.includes(country)) {
            setSelectedCountry(country);
        } else {
            setSelectedCountry("India");
        }
    }, [country]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData({
            ...formData,
            [name]: value,
        });

        setErrors({
            ...errors,
            [name]: "",
        });

        if (name === "country") {
            setSelectedCountry(value);
            setPhone("");

            setErrors((prev) => ({
                ...prev,
                phone: "",
                country: "",
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        let newErrors = {
            name: "",
            email: "",
            phone: "",
            country: "",
            firmType: "",
            message: "",
        };

        let isValid = true;

        if (!formData.name.trim()) {
            newErrors.name = "Name is required";
            isValid = false;
        }

        const emailRegex = /^\S+@\S+\.\S+$/;

        if (!formData.email.trim()) {
            newErrors.email = "Email is required";
            isValid = false;
        } else if (!emailRegex.test(formData.email.trim())) {
            newErrors.email = "Invalid email format";
            isValid = false;
        }

        if (!phone) {
            newErrors.phone = "Phone number is required";
            isValid = false;
        } else if (!isValidPhoneNumber(phone)) {
            newErrors.phone = "Please enter a valid phone number";
            isValid = false;
        }

        if (!selectedCountry) {
            newErrors.country = "Please select country";
            isValid = false;
        }

        if (formData.message.trim().length > 300) {
            newErrors.message = "Maximum character limit is 300";
            isValid = false;
        }

        setErrors(newErrors);

        if (!isValid) return;

        const payload = {
            name: formData.name,
            email: formData.email.trim(),
            phone: phone,
            lookingFor: "Manufacturing",
            queryPageIndex: "MANUFACTURING_AI_PAGE",
            message:
                (formData.message || "") +
                ` IP: ${country} dropdown: ${selectedCountry} | URL: ${websiteURL}`,
        };

        try {
            setLoading(true);
            setFormError("");

            const response = await fetch(ApiPath + "/tqenquiry", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                setFormData({
                    name: "",
                    email: "",
                    firmType: "Manufacturing",
                    message: "",
                });

                setPhone("");
                setFormVisible(false);

                setTimeout(() => {
                    setFormVisible(true);
                    setLoading(false);
                }, 5000);
            } else {
                setFormError("Something went wrong. Please try again.");
                setLoading(false);
            }
        } catch (error) {
            console.error(error);
            setFormError("Something went wrong. Please try again.");
            setLoading(false);
        }
    };

    const getCountryCode = (countryName) => {
        const map = {
            India: "IN",
            "United States": "US",
            Australia: "AU",
            Germany: "DE",
            "New Zealand": "NZ",
        };

        return map[countryName] || "IN";
    };

    const StatCard = ({ icon, label, value, subText }) => (
        <div className="rounded-[2rem] border border-white/40 bg-white/80 backdrop-blur-xl shadow-[0_20px_70px_rgba(15,23,42,0.08)] p-6 lg:p-7 hover:-translate-y-1 transition-all duration-300">
            <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
                {icon}
            </div>

            <p className="mt-5 text-xs uppercase tracking-[0.18em] text-slate-500 font-bold">
                {label}
            </p>

            <h3 className="mt-3 text-2xl xl:text-2xl font-black text-slate-900 leading-tight break-words">
                {value}
            </h3>

            <p className="mt-3 text-slate-500 leading-7 text-sm">
                {subText}
            </p>
        </div>
    );

    return (
        <>
            <Head />

            <div className="min-h-screen overflow-hidden bg-slate-50 text-slate-900 relative">
                {/* HERO */}
                <section className="relative py-20 lg:py-14 overflow-hidden">
                    <div className="absolute top-[-10rem] left-[-10rem] w-[30rem] h-[30rem] bg-blue-500/10 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-[-10rem] right-[-10rem] w-[30rem] h-[30rem] bg-indigo-500/10 rounded-full blur-3xl"></div>

                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 xl:gap-20 items-center">
                            <div className="lg:col-span-7 xl:col-span-7" data-aos="fade-right">
                                <div className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-blue-100 bg-blue-50 text-blue-700 text-sm font-bold">
                                    <Sparkles size={16} />
                                    AI Solutions For Manufacturing
                                </div>

                                <h1 className="mt-8 text-4xl sm:text-5xl xl:text-5xl font-black leading-[0.95] tracking-[-0.05em] text-slate-900">
                                    AI Automation
                                    <span className="block mt-3 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                                        For Smart Manufacturing Operations
                                    </span>
                                </h1>

                                <p className="mt-4 text-lg leading-9 text-slate-600 max-w-2xl">
                                    Improve manufacturing operations with AI-enabled production planning,
                                    inventory intelligence, warehouse optimization, computer vision inspection,
                                    supplier analytics, predictive maintenance and plant-level decision support.
                                </p>

                                <div className="mt-10 flex flex-col sm:flex-row gap-4">
                                    <a
                                        href="#contact-form"
                                        className="inline-flex items-center justify-center gap-2 rounded-2xl px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold shadow-xl hover:-translate-y-1 transition-all duration-300"
                                    >
                                        Get AI Consultation
                                        <ArrowRight size={18} />
                                    </a>

                                    <a
                                        href="/assets/pdfs/AI Feature Benefit Report for Manufacturing Industry.pdf"
                                        download
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center justify-center gap-2 rounded-2xl px-8 py-4 border border-slate-200 bg-white/80 backdrop-blur-xl text-slate-800 font-bold shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                                    >
                                        Download Report
                                        <Download size={18} />
                                    </a>
                                </div>
                            </div>

                            <div className="relative lg:col-span-5 xl:col-span-5" data-aos="fade-left">
                                <div className="relative rounded-[2.5rem] border border-white/40 bg-white/80 backdrop-blur-xl shadow-[0_30px_90px_rgba(15,23,42,0.12)] p-4">
                                    <img
                                        src="/assets/images/manufacturingAi.jpg"
                                        alt="Manufacturing AI"
                                        className="w-full rounded-[2rem] object-cover"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* BENEFITS */}
                <section className="py-20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid lg:grid-cols-12 gap-10 items-start">
                            <div className="lg:col-span-8" data-aos="fade-up">
                                <div className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-blue-100 bg-blue-50 text-blue-700 text-sm font-bold">
                                    <BarChart3 size={16} />
                                    AI Feature Benefits
                                </div>

                                <h2 className="mt-6 text-4xl lg:text-5xl font-black tracking-[-0.04em] leading-tight text-slate-900">
                                    Practical AI Benefits For Manufacturing Teams
                                </h2>

                                <p className="mt-6 text-lg leading-9 text-slate-600 max-w-3xl">
                                    This section explains how each AI feature supports real manufacturing workflows.
                                    The focus is on production visibility, quality improvement, inventory accuracy,
                                    procurement control, warehouse efficiency and machine reliability.
                                </p>

                                <div className="mt-8 flex flex-wrap gap-3">
                                    {[
                                        "Better production planning",
                                        "Improved quality control",
                                        "Smarter inventory visibility",
                                        "Reduced operational bottlenecks",
                                        "Stronger plant management visibility",
                                    ].map((chip, index) => (
                                        <div
                                            key={index}
                                            className="rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm"
                                        >
                                            {chip}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="lg:col-span-4" data-aos="zoom-in" data-aos-delay="100">
                                <div className="sticky top-28 rounded-[2rem] border border-white/40 bg-white/90 backdrop-blur-xl shadow-[0_20px_70px_rgba(15,23,42,0.08)] p-6 lg:p-8">
                                    <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
                                        <Factory size={30} />
                                    </div>

                                    <h3 className="mt-6 text-2xl font-black text-slate-900">
                                        AI Layer For Plant Operations
                                    </h3>

                                    <p className="mt-4 text-slate-600 leading-8">
                                        The AI system works as an operations assistant on top of existing ERP,
                                        Excel, machine data, warehouse processes and shop-floor workflows.
                                        It does not require replacing existing manufacturing systems.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* BENEFIT CARDS */}
                        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                            <div data-aos="fade-up" data-aos-delay="0">
                                <StatCard
                                    icon={<Factory size={24} />}
                                    label="Production Intelligence"
                                    value="Planning & Scheduling"
                                    subText="Supports demand forecasting, capacity planning, schedule optimization and bottleneck visibility."
                                />
                            </div>

                            <div data-aos="fade-up" data-aos-delay="100">
                                <StatCard
                                    icon={<BadgeCheck size={24} />}
                                    label="Quality Intelligence"
                                    value="Inspection & RCA"
                                    subText="Supports defect detection, anomaly alerts and faster root cause analysis."
                                />
                            </div>

                            <div data-aos="fade-up" data-aos-delay="200">
                                <StatCard
                                    icon={<ShieldCheck size={24} />}
                                    label="Secure Manufacturing AI"
                                    value="Private & Controlled"
                                    subText="Can be implemented with role-based access, audit logs and secure deployment options."
                                />
                            </div>
                        </div>

                        {/* TABLE */}
                        <div
                            className="mt-14 overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_20px_70px_rgba(15,23,42,0.06)]"
                            data-aos="fade-up"
                        >
                            <div className="px-6 lg:px-8 py-8 border-b border-slate-100">
                                <h3 className="text-2xl font-black text-slate-900">
                                    Feature-Wise AI Benefits Matrix
                                </h3>
                                <p className="mt-3 text-slate-600 leading-7">
                                    A clear business view of what each AI feature does and how it improves
                                    manufacturing operations.
                                </p>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="min-w-[1450px] w-full">
                                    <thead className="bg-gradient-to-r from-blue-600 to-indigo-600">
                                        <tr>
                                            <th className="px-6 py-5 text-left text-xs font-bold uppercase tracking-[0.18em] text-white">
                                                #
                                            </th>
                                            <th className="px-6 py-5 text-left text-xs font-bold uppercase tracking-[0.18em] text-white">
                                                AI Feature
                                            </th>
                                            <th className="px-6 py-5 text-left text-xs font-bold uppercase tracking-[0.18em] text-white">
                                                Purpose / Users
                                            </th>
                                            <th className="px-6 py-5 text-left text-xs font-bold uppercase tracking-[0.18em] text-white">
                                                Current Challenge
                                            </th>
                                            <th className="px-6 py-5 text-left text-xs font-bold uppercase tracking-[0.18em] text-white">
                                                What AI Does
                                            </th>
                                            <th className="px-6 py-5 text-left text-xs font-bold uppercase tracking-[0.18em] text-white">
                                                Manufacturing Benefit
                                            </th>
                                            <th className="px-6 py-5 text-left text-xs font-bold uppercase tracking-[0.18em] text-white">
                                                Implementation Detail
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {manufacturingFeatures.map((item) => (
                                            <tr key={item.id} className="hover:bg-slate-50 transition-all duration-200">
                                                <td className="px-6 py-5 border-b border-slate-100 align-top">
                                                    <span className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 font-black flex items-center justify-center">
                                                        {item.id}
                                                    </span>
                                                </td>

                                                <td className="px-6 py-5 border-b border-slate-100 align-top">
                                                    <div className="flex items-start gap-3">
                                                        <span className="mt-1 w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                                                            {item.icon}
                                                        </span>
                                                        <strong className="text-slate-900 leading-7 block">
                                                            {item.name}
                                                        </strong>
                                                    </div>
                                                </td>

                                                <td className="px-6 py-5 border-b border-slate-100 align-top text-slate-600 font-medium leading-7">
                                                    {item.purpose}
                                                </td>

                                                <td className="px-6 py-5 border-b border-slate-100 align-top text-slate-500 leading-7">
                                                    {item.currentChallenge}
                                                </td>

                                                <td className="px-6 py-5 border-b border-slate-100 align-top text-slate-500 leading-7">
                                                    {item.aiAction}
                                                </td>

                                                <td className="px-6 py-5 border-b border-slate-100 align-top">
                                                    <span className="inline-flex rounded-2xl bg-blue-50 px-4 py-3 text-sm font-bold text-blue-700 leading-6">
                                                        {item.benefit}
                                                    </span>
                                                </td>

                                                <td className="px-6 py-5 border-b border-slate-100 align-top text-slate-500 leading-7">
                                                    {item.implementation}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <p className="mt-3 text-xs text-slate-500 leading-6">
                            *The above feature matrix is for business discussion and solution planning.
                            Final implementation scope depends on plant size, machine count, ERP/database availability,
                            camera/sensor readiness, data quality, user roles and operational workflow requirements.
                        </p>
                    </div>
                </section>

                {/* VALUE AREAS */}
                <section className="py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center max-w-3xl mx-auto" data-aos="fade-up">
                            <div className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-blue-100 bg-blue-50 text-blue-700 text-sm font-bold">
                                <Sparkles size={16} />
                                AI Opportunity Areas
                            </div>

                            <h2 className="mt-6 text-4xl lg:text-5xl font-black tracking-[-0.04em] leading-tight text-slate-900">
                                Where AI Creates Value In Manufacturing
                            </h2>

                            <p className="mt-6 text-lg leading-9 text-slate-600">
                                Start with practical plant workflows and then expand into advanced production,
                                quality, warehouse and maintenance intelligence.
                            </p>
                        </div>

                        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                            {valueAreas.map((item, index) => (
                                <div
                                    key={index}
                                    className="rounded-[2rem] border border-slate-200 bg-slate-50 p-7 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                                    data-aos="fade-up"
                                    data-aos-delay={index * 100}
                                >
                                    <div className="w-14 h-14 rounded-2xl bg-blue-100 text-blue-600 flex items-center justify-center">
                                        {item.icon}
                                    </div>
                                    <h3 className="mt-6 text-xl font-black text-slate-900">
                                        {item.title}
                                    </h3>
                                    <p className="mt-4 text-slate-600 leading-7">
                                        {item.text}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* IMPLEMENTATION APPROACH */}
                <section className="py-20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center max-w-3xl mx-auto" data-aos="fade-up">
                            <div className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-blue-100 bg-blue-50 text-blue-700 text-sm font-bold">
                                <Settings size={16} />
                                Implementation Approach
                            </div>

                            <h2 className="mt-6 text-4xl lg:text-5xl font-black tracking-[-0.04em] leading-tight text-slate-900">
                                AI Layer On Top Of Existing Manufacturing Systems
                            </h2>

                            <p className="mt-6 text-lg leading-9 text-slate-600">
                                The solution can be implemented in phases without disturbing existing ERP,
                                Excel, production, inventory and maintenance workflows.
                            </p>
                        </div>

                        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                            {implementationAreas.map((item, index) => (
                                <div
                                    key={index}
                                    className="rounded-[2rem] border border-white/40 bg-white/80 backdrop-blur-xl shadow-[0_20px_70px_rgba(15,23,42,0.08)] p-7 hover:-translate-y-1 transition-all duration-300"
                                    data-aos="fade-up"
                                    data-aos-delay={index * 100}
                                >
                                    <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
                                        {item.icon}
                                    </div>
                                    <h3 className="mt-6 text-xl font-black text-slate-900">
                                        {item.title}
                                    </h3>
                                    <p className="mt-4 text-slate-600 leading-7">
                                        {item.text}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CONTACT FORM SECTION */}
                <section id="contact-form" className="relative py-20 sm:py-24 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-indigo-50"></div>

                    <div className="relative max-w-7xl mx-auto px-0 md:px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                            <div className="px-6" data-aos="fade-right">
                                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 text-blue-700 font-semibold text-sm border border-blue-200">
                                    <Sparkles size={16} />
                                    AI Consultation For Manufacturing
                                </div>

                                <h2 className="mt-6 text-4xl sm:text-5xl font-black leading-tight text-slate-900">
                                    Let’s Build
                                    <span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                                        AI For Your Manufacturing Business
                                    </span>
                                </h2>

                                <p className="mt-6 text-lg text-slate-600 leading-8 max-w-xl">
                                    Automate production workflows, improve inventory visibility, strengthen quality inspection,
                                    reduce operational bottlenecks and modernize manufacturing processes using AI-powered systems.
                                </p>

                                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {[
                                        "Improve production visibility",
                                        "Optimize inventory planning",
                                        "Strengthen quality control",
                                        "Enable predictive maintenance",
                                    ].map((item, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center gap-3 bg-white border border-slate-200 rounded-2xl px-5 py-4 shadow-sm"
                                        >
                                            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600">
                                                <CheckCircle size={18} />
                                            </div>
                                            <span className="font-medium text-slate-700">{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="relative" data-aos="fade-left" data-aos-delay="100">
                                <div className="absolute -inset-2 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-[2rem] blur-xl opacity-20"></div>

                                <div className="relative bg-white/90 backdrop-blur-xl border border-white/40 md:rounded-[2rem] shadow-[0_25px_80px_rgba(0,0,0,0.08)] overflow-hidden">
                                    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-8 text-white">
                                        <div className="flex items-center gap-4">
                                            <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center backdrop-blur-md">
                                                <Factory size={28} />
                                            </div>
                                            <div>
                                                <h3 className="text-2xl font-bold">Book AI Consultation</h3>
                                                <p className="text-blue-100 mt-1 text-sm">
                                                    Tell us about your manufacturing automation requirements
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="p-6 sm:p-8">
                                        {!formVisible && (
                                            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-5 py-6 rounded-2xl mb-6">
                                                <div className="flex flex-col items-center text-center">
                                                    <CheckCircle size={52} className="text-blue-600 mb-4" />
                                                    <h3 className="text-xl font-bold mb-2">Thank You!</h3>
                                                    <p className="text-sm sm:text-base">Our team will contact you shortly.</p>
                                                </div>
                                            </div>
                                        )}

                                        {formError && (
                                            <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-6 rounded-2xl mb-6">
                                                <div className="flex flex-col items-center text-center">
                                                    <XCircle size={48} className="text-red-500 mb-3" />
                                                    <h3 className="text-lg font-bold mb-2">Submission Failed</h3>
                                                    <p className="text-sm">{formError}</p>
                                                </div>
                                            </div>
                                        )}

                                        {formVisible && (
                                            <form onSubmit={handleSubmit} className="space-y-5">
                                                <div>
                                                    <input
                                                        type="text"
                                                        name="name"
                                                        placeholder="Full Name"
                                                        value={formData.name}
                                                        onChange={handleChange}
                                                        className={`w-full rounded-2xl border bg-slate-50 px-5 py-4 outline-none transition-all duration-300 ${errors.name
                                                                ? "border-red-500"
                                                                : "border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                                                            }`}
                                                    />
                                                    {errors.name && <p className="text-red-500 text-sm mt-2">{errors.name}</p>}
                                                </div>

                                                <div>
                                                    <div
                                                        className={`rounded-2xl border bg-slate-50 px-4 py-4 transition-all duration-300 ${errors.phone
                                                                ? "border-red-500"
                                                                : "border-slate-200 focus-within:border-blue-500 focus-within:bg-white focus-within:ring-4 focus:ring-blue-100"
                                                            }`}
                                                    >
                                                        <PhoneInput
                                                            key={selectedCountry}
                                                            countries={["IN", "US", "AU", "DE", "JP", "SG", "NZ"]}
                                                            defaultCountry={getCountryCode(selectedCountry)}
                                                            international
                                                            value={phone}
                                                            onChange={(value) => {
                                                                setPhone(value || "");
                                                                setErrors({ ...errors, phone: "" });
                                                            }}
                                                            placeholder="Enter phone number"
                                                        />
                                                    </div>
                                                    {errors.phone && <p className="text-red-500 text-sm mt-2">{errors.phone}</p>}
                                                </div>

                                                <div>
                                                    <input
                                                        type="email"
                                                        name="email"
                                                        placeholder="Email Address"
                                                        value={formData.email}
                                                        onChange={handleChange}
                                                        className={`w-full rounded-2xl border bg-slate-50 px-5 py-4 outline-none transition-all duration-300 ${errors.email
                                                                ? "border-red-500"
                                                                : "border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                                                            }`}
                                                    />
                                                    {errors.email && <p className="text-red-500 text-sm mt-2">{errors.email}</p>}
                                                </div>

                                                <div>
                                                    <select
                                                        id="country"
                                                        name="country"
                                                        className={`w-full rounded-2xl border bg-slate-50 px-5 py-4 outline-none transition-all duration-300 ${errors.country
                                                                ? "border-red-500"
                                                                : "border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                                                            }`}
                                                        onChange={handleChange}
                                                        value={selectedCountry}
                                                    >
                                                        {countryOptions.map((countryName) => (
                                                            <option key={countryName} value={countryName}>
                                                                {countryName}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {errors.country && <p className="text-red-500 text-sm mt-2">{errors.country}</p>}
                                                </div>

                                                <div>
                                                    <textarea
                                                        rows={3}
                                                        name="message"
                                                        placeholder="Tell us which manufacturing workflows, operational bottlenecks, quality issues, inventory processes or factory systems you want to automate"
                                                        value={formData.message}
                                                        onChange={handleChange}
                                                        className={`w-full rounded-2xl bg-slate-50 px-5 py-4 outline-none transition-all duration-300 resize-none ${errors.message
                                                                ? "border border-red-500"
                                                                : "border border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                                                            }`}
                                                    />
                                                    {errors.message && <p className="text-red-500 text-sm mt-2">{errors.message}</p>}
                                                </div>

                                                <button
                                                    type="submit"
                                                    disabled={loading}
                                                    className={`w-full inline-flex items-center justify-center gap-2 rounded-2xl py-4 text-lg font-semibold text-white transition-all duration-300 ${loading
                                                            ? "bg-slate-400 cursor-not-allowed"
                                                            : "bg-gradient-to-r from-blue-600 to-indigo-600 shadow-xl hover:scale-[1.02]"
                                                        }`}
                                                >
                                                    {loading ? "Submitting..." : "Request AI Consultation"}
                                                    {!loading && <ArrowRight size={18} />}
                                                </button>
                                            </form>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <FooterNew />
            </div>
        </>
    );
};

export default ManufacturingAI;